import React, { useState } from 'react';
import { Card, Badge, Button, Collapse } from 'react-bootstrap';
import { stringToGradient } from '../utils/colorUtils';
import './PathDisplay.css';

const PathDisplay = ({ paths, entityOneId, entityTwoId, pathCount }) => {
  const [expandedPaths, setExpandedPaths] = useState(new Set());

  // 按score降序排序路径
  const sortedPaths = paths ? [...paths].sort((a, b) => (b.score || 0) - (a.score || 0)) : [];

  // 切换路径展开状态
  const togglePath = (pathIndex) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(pathIndex)) {
      newExpanded.delete(pathIndex);
    } else {
      newExpanded.add(pathIndex);
    }
    setExpandedPaths(newExpanded);
  };

  // 展开所有路径
  const expandAll = () => {
    setExpandedPaths(new Set(sortedPaths.map((_, index) => index)));
  };

  // 收起所有路径
  const collapseAll = () => {
    setExpandedPaths(new Set());
  };

  // 获取路径长度（跳数）
  const getPathLength = (path) => {
    if (path && path.path_nodes && Array.isArray(path.path_nodes)) {
      return path.path_nodes.length - 1;
    }
    return 0;
  };

  // 获取路径类型的颜色
  const getPathTypeColor = (length) => {
    switch (length) {
      case 1: return 'primary';     // 蓝色 - 直接连接
      case 2: return 'success';     // 绿色 - 2跳
      case 3: return 'info';        // 青色 - 3跳
      case 4: return 'warning';     // 橙色 - 4跳
      case 5: return 'danger';      // 红色 - 5跳
      case 6: return 'dark';        // 深色 - 6跳
      case 7: return 'light';       // 浅灰色 - 7跳
      case 8: return 'purple';      // 紫色 - 8跳
      case 9: return 'pink';        // 粉色 - 9跳
      case 10: return 'teal';       // 青绿色 - 10跳
      default: return 'secondary';  // 灰色 - 其他情况
    }
  };

  if (!sortedPaths || sortedPaths.length === 0) {
    return (
      <Card className="path-display-card">
        <Card.Header>
          <h5 className="mb-0">Connection Paths</h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted">No paths found between the specified entities.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="path-display-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">Connection Paths</h5>
          <small className="text-muted">
            Found {pathCount} paths between entities
          </small>
        </div>
        <div>
          <Button variant="outline-primary" size="sm" onClick={expandAll} className="me-2">
            Expand All
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="path-display-body">
        {sortedPaths.map((path, pathIndex) => {
          const pathLength = getPathLength(path);
          const isExpanded = expandedPaths.has(pathIndex);
          
          // 使用新的数据格式
          const pathNodes = path.path_nodes || [];
          const pathTypes = path.path_types || [];
          const relations = path.relations || [];
          const score = path.score || 0;
          
          return (
            <div key={pathIndex} className="path-item" data-hop-count={pathLength}>
              <div 
                className="path-header"
                onClick={() => togglePath(pathIndex)}
                style={{ cursor: 'pointer' }}
              >
                <div className="path-info">
                  <Badge bg={getPathTypeColor(pathLength)} className="me-2">
                    {pathLength}-hop
                  </Badge>
                  <span className="path-title">Path {pathIndex + 1}</span>
                  <small className="text-muted ms-2">
                    ({pathNodes.length} entities)
                  </small>
                  <Badge bg="info" className="ms-2">
                    Score: {score.toFixed(3)}
                  </Badge>
                </div>
                <div className="path-toggle">
                  <span>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>
              
              <Collapse in={isExpanded}>
                <div className="path-content">
                  <div className="path-visualization">
                    {pathNodes.map((entityName, entityIndex) => {
                      const backgroundColor = stringToGradient(entityName);
                      const typeBackgroundColor = pathTypes[entityIndex] ? stringToGradient(pathTypes[entityIndex]) : '#f8f9fa';
                      // 固定使用白色文字以保持亮色
                      const textColor = '#ffffff';
                      
                      return (
                          <React.Fragment key={entityIndex}>
                            <div className="entity-node-container">
                              <div 
                                className="entity-name-section"
                                style={{
                                  background: backgroundColor,
                                  color: textColor
                                }}
                              >
                                <div className="entity-name">{entityName}</div>
                              </div>
                              {pathTypes[entityIndex] && (
                                <div 
                                  className="entity-type-section"
                                  style={{
                                    background: typeBackgroundColor,
                                    color: textColor
                                  }}
                                >
                                  <div className="entity-type">{pathTypes[entityIndex]}</div>
                                </div>
                              )}
                            </div>
                            {entityIndex < pathNodes.length - 1 && (
                              <div className="path-connection">
                                <div className="path-arrow">
                                  <span>{relations[entityIndex] === 'associated_with' ? '↔' : '→'}</span>
                                </div>
                                {relations[entityIndex] && (
                                  <div className="relation-label">
                                    {relations[entityIndex]}
                                  </div>
                                )}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </div>
                </div>
              </Collapse>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

export default PathDisplay; 